import { createSlice } from '@reduxjs/toolkit'
import i18next from 'i18next'
import themeConfig from '@/theme.config'
import { getBrowserTitle } from '@/utils/constants'
import {
  applyResolvedThemeToDom,
  normalizeStoredThemePreference,
  type ResolvedTheme,
  readStoredThemePreference,
  resolveTheme,
  type ThemePreference,
} from '@/utils/theme'

const getStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(key)
}

const setStorageItem = (key: string, value: string) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(key, value)
}

const removeStorageItem = (key: string) => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(key)
}

if (getStorageItem('semidark') !== null) {
  removeStorageItem('semidark')
}

export type ThemeConfigState = {
  theme: ThemePreference
  resolvedTheme: ResolvedTheme
  menu: string
  layout: string
  rtlClass: string
  animation: string
  navbar: string
  locale: string
  isDarkMode: boolean
  sidebar: boolean
  pageTitle: string
  languageList: { code: string; name: string }[]
}

const initialThemePreference = readStoredThemePreference(
  'theme',
  normalizeStoredThemePreference(themeConfig.theme),
)
const initialResolvedTheme = resolveTheme(initialThemePreference)

const initialState: ThemeConfigState = {
  theme: initialThemePreference,
  resolvedTheme: initialResolvedTheme,
  menu: getStorageItem('menu') || themeConfig.menu,
  layout: getStorageItem('layout') || themeConfig.layout,
  rtlClass: getStorageItem('rtlClass') || themeConfig.rtlClass,
  animation: getStorageItem('animation') || themeConfig.animation,
  navbar: getStorageItem('navbar') || themeConfig.navbar,
  locale: getStorageItem('i18nextLng') || themeConfig.locale,
  isDarkMode: initialResolvedTheme === 'dark',
  sidebar: getStorageItem('sidebar') === 'true',
  pageTitle: '',
  languageList: [
    { code: 'zh', name: 'Chinese' },
    { code: 'da', name: 'Danish' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'el', name: 'Greek' },
    { code: 'hu', name: 'Hungarian' },
    { code: 'it', name: 'Italian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'pl', name: 'Polish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
    { code: 'sv', name: 'Swedish' },
    { code: 'tr', name: 'Turkish' },
    { code: 'ae', name: 'Arabic' },
  ],
}

const applyThemeToState = (state: ThemeConfigState, preference: ThemePreference) => {
  const resolvedTheme = resolveTheme(preference)
  state.theme = preference
  state.resolvedTheme = resolvedTheme
  state.isDarkMode = resolvedTheme === 'dark'
  applyResolvedThemeToDom(resolvedTheme)
}

const themeConfigSlice = createSlice({
  name: 'themeConfig',
  initialState,
  reducers: {
    toggleTheme(state, { payload }) {
      const nextPreference = normalizeStoredThemePreference(payload ?? state.theme)
      setStorageItem('theme', nextPreference)
      applyThemeToState(state, nextPreference)
    },
    syncSystemTheme(state) {
      if (state.theme !== 'system') return
      applyThemeToState(state, state.theme)
    },
    toggleMenu(state, { payload }) {
      state.sidebar = false
      state.menu = payload || state.menu
      setStorageItem('menu', state.menu)
    },
    toggleLayout(state, { payload }) {
      state.layout = payload || state.layout
      setStorageItem('layout', state.layout)
    },
    toggleRTL(state, { payload }) {
      state.rtlClass = payload || state.rtlClass
      setStorageItem('rtlClass', state.rtlClass)
      document.querySelector('html')?.setAttribute('dir', state.rtlClass || 'ltr')
    },
    toggleAnimation(state, { payload }) {
      state.animation = (payload || state.animation || '').trim()
      setStorageItem('animation', state.animation)
    },
    toggleNavbar(state, { payload }) {
      state.navbar = payload || state.navbar
      setStorageItem('navbar', state.navbar)
    },
    toggleLocale(state, { payload }) {
      const locale = payload || state.locale
      i18next.changeLanguage(locale)
      state.locale = locale
    },
    toggleSidebar(state) {
      state.sidebar = !state.sidebar
      setStorageItem('sidebar', `${state.sidebar}`)
    },
    setPageTitle(state, { payload }) {
      state.pageTitle = payload || ''
      document.title = getBrowserTitle(`${payload} | DORA - Bonds Marketplace`)
    },
  },
})

export const {
  toggleTheme,
  syncSystemTheme,
  toggleMenu,
  toggleLayout,
  toggleRTL,
  toggleAnimation,
  toggleNavbar,
  toggleLocale,
  toggleSidebar,
  setPageTitle,
} = themeConfigSlice.actions

export default themeConfigSlice.reducer
